/** Who a failed page render is about: the document, or us.
 *
 *  Its own module, free of pdf.js, so the rule can be tested without a PDF
 *  engine - which the test environment cannot load anyway. */

export type RenderFailure =
  /** Password-protected; pdf.js will not open it and neither will we. */
  | 'encrypted'
  /** Not a PDF, or damaged past the point of a page tree. */
  | 'malformed'
  /** The renderer itself could not start, or failed for a reason that is not a
   *  statement about the document. */
  | 'unavailable';

/** Classify a render failure without guessing.
 *
 *  Only the exceptions pdf.js raises about the document may be reported as
 *  findings about the document. Anything else - a renderer that would not
 *  start, a canvas that could not be had, a fault in this worker - is ours and
 *  is reported as ours. Blaming the sample for a platform fault is the mistake
 *  this function exists to prevent. */
export function failureOf(error: unknown): RenderFailure {
  const name = (error as { name?: unknown } | null)?.name;
  if (name === 'PasswordException') return 'encrypted';
  if (name === 'InvalidPDFException' || name === 'MissingPDFException') return 'malformed';
  return 'unavailable';
}

/** What each failure means in the analyst's terms. "encrypted" is the same
 *  trick a password-protected archive plays: keep the scanner out, let the
 *  victim in with the password from the mail body. */
export function renderFailureMessage(reason: RenderFailure): string {
  switch (reason) {
    case 'encrypted':
      return 'The document is password-protected, so its pages cannot be rendered.';
    case 'malformed':
      return 'The document is damaged past the point where pages can be laid out.';
    case 'unavailable':
      // Deliberately about US, not the document.
      return 'The page renderer could not run. This is a platform fault, not a finding about the document.';
  }
}
